// Context Card editor — create, edit, and put Claude to work on either.
//
// Cards are standing research: a person, a treaty, an institution, an
// operation. They outlive the articles that cite them, so this screen is an
// editing surface first and a generation toy second — the AI drafts and
// refines, the editor always commits.
//
//   POST  /news/editor/context-cards                  — create
//   PATCH /news/editor/context-cards/:cardId          — edit, any status
//   POST  /news/editor/context-cards/draft            — Claude drafts from a prompt
//   POST  /news/editor/context-cards/:cardId/refine   — Claude revises with instructions
//
// THE ONE SHARP EDGE: /refine reads the card from the DATABASE, not from this
// form. Refining on top of unsaved edits would hand Claude the old text and
// then overwrite the new — losing work silently, which is the worst way to
// lose it. So refine is disabled while the form is dirty and says why.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select, Textarea } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";
import { describeApiError, formatDate, type ContextCardRow } from "./index";

type Status = "draft" | "published" | "archived";

interface Form {
  title: string;
  slug: string;
  category: string;
  body: string;
  key_entities: string;
  external_url: string;
  external_label: string;
  source_notes: string;
  primary_entity_id: string | null;
  image_url: string;
  image_credit: string;
  image_license: string;
  status: Status;
}

/** A candidate subject from the entity search. */
interface EntityHit {
  id: string;
  name: string;
  entity_type: string;
  image_url: string | null;
}

const BLANK: Form = {
  title: "",
  slug: "",
  category: "",
  body: "",
  key_entities: "",
  external_url: "",
  external_label: "",
  source_notes: "",
  primary_entity_id: null,
  image_url: "",
  image_credit: "",
  image_license: "",
  status: "draft",
};

const toForm = (c: ContextCardRow): Form => ({
  title: c.title,
  slug: c.slug,
  category: c.category,
  body: c.body,
  key_entities: (c.key_entities ?? []).join(", "),
  external_url: c.external_url ?? "",
  external_label: c.external_label ?? "",
  source_notes: c.source_notes ?? "",
  primary_entity_id: c.primary_entity_id ?? null,
  image_url: c.image_url ?? "",
  image_credit: c.image_credit ?? "",
  image_license: c.image_license ?? "",
  status: c.status,
});

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 200);
}

export default function ContextCardEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === "new";

  const [form, setForm] = useState<Form>(BLANK);
  const [saved, setSaved] = useState<Form>(BLANK);
  const [meta, setMeta] = useState<ContextCardRow | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The slug is derived from the title until an editor touches it, then it is
  // theirs. A published card's slug is its identity, so it is never re-derived.
  const [slugTouched, setSlugTouched] = useState(!isNew);

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiInstructions, setAiInstructions] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const set = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const card = await api<ContextCardRow>(
        `/news/editor/context-cards/${id}`,
      );
      setMeta(card);
      setForm(toForm(card));
      setSaved(toForm(card));
    } catch (e: any) {
      setError(describeApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(saved),
    [form, saved],
  );

  const canSave =
    form.title.trim() &&
    form.slug.trim() &&
    form.category.trim() &&
    form.body.trim() &&
    // Mirrors the CHECK on the table, so the error arrives before the request.
    (!form.image_url.trim() || !!form.image_credit.trim());

  function payload() {
    return {
      title: form.title.trim(),
      slug: form.slug.trim(),
      category: form.category.trim(),
      body: form.body.trim(),
      key_entities: form.key_entities
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      external_url: form.external_url.trim() || null,
      external_label: form.external_label.trim() || null,
      source_notes: form.source_notes.trim() || null,
      primary_entity_id: form.primary_entity_id,
      image_url: form.image_url.trim() || null,
      image_credit: form.image_credit.trim() || null,
      image_license: form.image_license.trim() || null,
      status: form.status,
    };
  }

  async function save(nextStatus?: Status) {
    setSaving(true);
    setError(null);
    setNotice(null);
    const body = { ...payload(), status: nextStatus ?? form.status };
    try {
      if (isNew) {
        const created = await api<ContextCardRow>(
          "/news/editor/context-cards",
          {
            method: "POST",
            body,
          },
        );
        navigate(`/context-cards/${created.id}`, { replace: true });
        return;
      }
      const updated = await api<ContextCardRow>(
        `/news/editor/context-cards/${id}`,
        { method: "PATCH", body },
      );
      setMeta(updated);
      setForm(toForm(updated));
      setSaved(toForm(updated));
      setNotice(
        nextStatus && nextStatus !== saved.status
          ? `Saved and moved to ${nextStatus}.`
          : "Saved.",
      );
    } catch (e: any) {
      setError(describeApiError(e));
    } finally {
      setSaving(false);
    }
  }

  /** Claude writes a card from scratch. Fills the form; nothing is persisted. */
  async function draftWithAI() {
    if (!aiPrompt.trim()) return;
    setAiBusy(true);
    setError(null);
    try {
      const r = await api<{ draft: any }>("/news/editor/context-cards/draft", {
        method: "POST",
        body: { prompt: aiPrompt.trim() },
      });
      const d = r.draft ?? {};
      setForm((p) => ({
        ...p,
        title: d.title ?? p.title,
        slug: d.slug ?? p.slug,
        category: d.category ?? p.category,
        body: d.body ?? p.body,
        key_entities: Array.isArray(d.key_entities)
          ? d.key_entities.join(", ")
          : p.key_entities,
        external_url: d.suggested_external_url ?? p.external_url,
        external_label: d.suggested_external_label ?? p.external_label,
      }));
      setSlugTouched(true);
      setNotice("Drafted — read it through, then save.");
    } catch (e: any) {
      setError(describeApiError(e));
    } finally {
      setAiBusy(false);
    }
  }

  /** Claude revises the SAVED card. Result lands in the form for review. */
  async function refineWithAI() {
    if (!aiInstructions.trim() || dirty) return;
    setAiBusy(true);
    setError(null);
    try {
      const r = await api<{ refined: any }>(
        `/news/editor/context-cards/${id}/refine`,
        { method: "POST", body: { instructions: aiInstructions.trim() } },
      );
      const d = r.refined ?? {};
      setForm((p) => ({
        ...p,
        title: d.title ?? p.title,
        // The server prompt preserves the slug unless asked; trust the form's.
        category: d.category ?? p.category,
        body: d.body ?? p.body,
        key_entities: Array.isArray(d.key_entities)
          ? d.key_entities.join(", ")
          : p.key_entities,
      }));
      setAiInstructions("");
      setNotice("Refined — review the changes, then save.");
    } catch (e: any) {
      setError(describeApiError(e));
    } finally {
      setAiBusy(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div style={styles.crumb}>
        <Link to="/context-cards" style={styles.crumbLink}>
          ← Context Cards
        </Link>
      </div>

      <header style={styles.header}>
        <div style={{ minWidth: 0 }}>
          <h1 style={styles.h1}>
            {isNew ? "New context card" : form.title || "Untitled card"}
          </h1>
          {meta && (
            <CardSubtle style={{ margin: "4px 0 0" }}>
              <StatusDot status={meta.status} /> {meta.status} · updated{" "}
              {formatDate(meta.updated_at)} · <code>{meta.slug}</code>
            </CardSubtle>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {dirty && (
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              Unsaved changes
            </span>
          )}
          <Button onClick={() => save()} disabled={!canSave || saving}>
            {saving ? "Saving…" : isNew ? "Create card" : "Save"}
          </Button>
          {!isNew && form.status !== "published" && (
            <Button
              variant="secondary"
              onClick={() => save("published")}
              disabled={!canSave || saving}
            >
              Save &amp; publish
            </Button>
          )}
        </div>
      </header>

      {error && (
        <Card style={{ marginBottom: 12, borderColor: "var(--danger)" }}>
          <span style={{ color: "var(--danger)" }}>{error}</span>
        </Card>
      )}
      {notice && (
        <Card style={{ marginBottom: 12 }}>
          <CardSubtle style={{ margin: 0 }}>{notice}</CardSubtle>
        </Card>
      )}

      <div style={styles.cols}>
        {/* ── the card itself ─────────────────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card style={{ marginBottom: 16 }}>
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => {
                set("title", e.target.value);
                if (!slugTouched) set("slug", slugify(e.target.value));
              }}
              placeholder="1929 Anglo-Egyptian Treaty"
            />

            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <Label>Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  placeholder="Colonial-era agreement"
                />
              </div>
              <div style={{ flex: 1 }}>
                <Label>Slug</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    set("slug", slugify(e.target.value));
                  }}
                  placeholder="1929-anglo-egyptian-treaty"
                />
              </div>
            </div>
            {!isNew && form.slug !== saved.slug && (
              <CardSubtle style={{ color: "var(--danger)", marginTop: 4 }}>
                Changing the slug of an existing card breaks any link that used
                the old one.
              </CardSubtle>
            )}

            <Label style={{ marginTop: 12 }}>Body — markdown</Label>
            <Textarea
              value={form.body}
              onChange={(e) => set("body", e.target.value)}
              rows={22}
              style={{ fontFamily: "ui-monospace, monospace", fontSize: 13 }}
              placeholder="What it is, who benefits, who is harmed, why it matters now."
            />
            <CardSubtle style={{ marginTop: 4 }}>
              {wordCount(form.body)} words. Readers open this inline beside an
              article, so the opening sentence carries the most weight.
            </CardSubtle>
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Reference</CardTitle>
            <Label style={{ marginTop: 10 }}>Key entities</Label>
            <Input
              value={form.key_entities}
              onChange={(e) => set("key_entities", e.target.value)}
              placeholder="Egypt, Sudan, United Kingdom, Nile"
            />
            <CardSubtle style={{ marginTop: 4 }}>
              Comma separated. Auto-attachment rules match on these, so they are
              how a card finds its way onto an article by itself.
            </CardSubtle>

            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <div style={{ flex: 2 }}>
                <Label>External link</Label>
                <Input
                  value={form.external_url}
                  onChange={(e) => set("external_url", e.target.value)}
                  placeholder="https://…"
                />
              </div>
              <div style={{ flex: 1 }}>
                <Label>Link label</Label>
                <Input
                  value={form.external_label}
                  onChange={(e) => set("external_label", e.target.value)}
                  placeholder="Read the treaty text"
                />
              </div>
            </div>

            <Label style={{ marginTop: 12 }}>Subject</Label>
            <EntityPicker
              value={form.primary_entity_id}
              current={meta?.primary_entity ?? null}
              onPick={(e) => set("primary_entity_id", e?.id ?? null)}
            />
            <CardSubtle style={{ marginTop: 4 }}>
              The one entity this card is chiefly about. Its portrait is used
              when the card has no image of its own, so a person only needs
              photographing once.
            </CardSubtle>

            <Label style={{ marginTop: 14 }}>Image — overrides the subject's</Label>
            <Input
              value={form.image_url}
              onChange={(e) => set("image_url", e.target.value)}
              placeholder="https://… — leave empty to use the subject's portrait"
            />
            {form.image_url.trim() ? (
              <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                <div style={{ flex: 2 }}>
                  <Label>Credit — required</Label>
                  <Input
                    value={form.image_credit}
                    onChange={(e) => set("image_credit", e.target.value)}
                    placeholder="Reuters / Jane Doe"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Label>Licence</Label>
                  <Input
                    value={form.image_license}
                    onChange={(e) => set("image_license", e.target.value)}
                    placeholder="CC BY-SA 4.0"
                  />
                </div>
              </div>
            ) : null}
            {form.image_url.trim() && !form.image_credit.trim() ? (
              <CardSubtle style={{ color: "var(--danger)", marginTop: 4 }}>
                An image needs a credit. An uncredited photograph is what draws
                a takedown.
              </CardSubtle>
            ) : null}
            <ImagePreview
              url={form.image_url.trim() || meta?.image?.url || null}
              from={form.image_url.trim() ? "card" : (meta?.image?.from ?? null)}
              credit={form.image_url.trim() ? form.image_credit : (meta?.image?.credit ?? null)}
            />

            <Label style={{ marginTop: 14 }}>Source notes — internal</Label>
            <Textarea
              value={form.source_notes}
              onChange={(e) => set("source_notes", e.target.value)}
              rows={4}
              placeholder="Where this came from, what is contested, what to check before relying on it."
            />
            <CardSubtle style={{ marginTop: 4 }}>
              Never shown to readers.
            </CardSubtle>
          </Card>
        </div>

        {/* ── sidebar ─────────────────────────────────────────────────── */}
        <div style={styles.side}>
          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Status</CardTitle>
            <Select
              value={form.status}
              onChange={(e) => set("status", e.target.value as Status)}
              style={{ marginTop: 10 }}
            >
              <option value="draft">Draft — not attachable</option>
              <option value="published">Published — in use</option>
              <option value="archived">Archived — retired</option>
            </Select>
            <CardSubtle style={{ marginTop: 8 }}>
              Only published cards can be attached to an article or matched by a
              rule. Archiving leaves existing attachments alone.
            </CardSubtle>
          </Card>

          <Card>
            <CardTitle>{isNew ? "Draft with AI" : "Refine with AI"}</CardTitle>
            {isNew ? (
              <>
                <CardSubtle style={{ marginTop: 8 }}>
                  Describe the subject. Claude researches it and fills the form
                  — nothing is saved until you say so.
                </CardSubtle>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={4}
                  style={{ marginTop: 10 }}
                  placeholder="The 1959 Nile Waters Agreement between Egypt and Sudan"
                />
                <Button
                  onClick={draftWithAI}
                  disabled={!aiPrompt.trim() || aiBusy}
                  style={{ marginTop: 10, width: "100%" }}
                >
                  {aiBusy ? "Drafting…" : "Draft card"}
                </Button>
              </>
            ) : (
              <>
                <CardSubtle style={{ marginTop: 8 }}>
                  Say what to change. The result lands in the form for you to
                  read before saving.
                </CardSubtle>
                <Textarea
                  value={aiInstructions}
                  onChange={(e) => setAiInstructions(e.target.value)}
                  rows={4}
                  style={{ marginTop: 10 }}
                  disabled={dirty}
                  placeholder="Add the 2024 Entebbe ratifications and cut the final paragraph."
                />
                {dirty ? (
                  <CardSubtle style={{ color: "var(--danger)", marginTop: 8 }}>
                    Save first. Refining works from the saved card, so it would
                    overwrite your unsaved edits with an older version.
                  </CardSubtle>
                ) : null}
                <Button
                  onClick={refineWithAI}
                  disabled={!aiInstructions.trim() || aiBusy || dirty}
                  style={{ marginTop: 10, width: "100%" }}
                >
                  {aiBusy ? "Refining…" : "Refine"}
                </Button>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

/** Search-and-pick the one entity a card is about. */
function EntityPicker({
  value,
  current,
  onPick,
}: {
  value: string | null;
  current: EntityHit | null;
  onPick: (e: EntityHit | null) => void;
}) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<EntityHit[]>([]);
  const [picked, setPicked] = useState<EntityHit | null>(current);

  useEffect(() => {
    setPicked(current);
  }, [current]);

  useEffect(() => {
    if (!q.trim()) {
      setHits([]);
      return;
    }
    const t = setTimeout(() => {
      api<{ entities: EntityHit[] }>(
        `/news/editor/graph/entities?limit=8&q=${encodeURIComponent(q.trim())}`,
      )
        .then((r) => setHits(r.entities))
        .catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  if (value && picked) {
    return (
      <div style={styles.picked}>
        {picked.image_url ? (
          <img src={picked.image_url} alt="" style={styles.pickedThumb} />
        ) : (
          <div style={{ ...styles.pickedThumb, ...styles.pickedThumbEmpty }}>
            no image
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{picked.name}</div>
          <CardSubtle style={{ margin: 0 }}>
            {picked.entity_type}
            {!picked.image_url &&
              " · no portrait yet — add one on the Graph page"}
          </CardSubtle>
        </div>
        <button
          type="button"
          onClick={() => {
            setPicked(null);
            onPick(null);
          }}
          style={styles.linkBtn}
        >
          change
        </button>
      </div>
    );
  }

  return (
    <>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search entities — Barack Obama, ECOWAS, Grand Ethiopian Renaissance Dam…"
      />
      {hits.length > 0 && (
        <div style={styles.hits}>
          {hits.map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => {
                setPicked(h);
                onPick(h);
                setQ("");
                setHits([]);
              }}
              style={styles.hit}
            >
              {h.name}
              <span style={{ color: "var(--text-muted)" }}> · {h.entity_type}</span>
              {h.image_url ? (
                <span style={{ color: "var(--success)" }}> · has portrait</span>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Shows what will actually be rendered, and where it came from. */
function ImagePreview({
  url,
  from,
  credit,
}: {
  url: string | null;
  from: "card" | "entity" | null;
  credit: string | null;
}) {
  if (!url) return null;
  return (
    <div style={styles.preview}>
      <img src={url} alt="" style={styles.previewImg} />
      <CardSubtle style={{ margin: 0 }}>
        {from === "entity"
          ? "Inherited from the subject."
          : "This card's own image."}
        {credit ? ` ${credit}` : ""}
      </CardSubtle>
    </div>
  );
}

function StatusDot({ status }: { status: Status }) {
  const color =
    status === "published"
      ? "var(--success)"
      : status === "draft"
        ? "var(--accent)"
        : "var(--text-muted)";
  return (
    <span
      style={{
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: 999,
        background: color,
        marginRight: 2,
      }}
    />
  );
}

function wordCount(s: string): number {
  const t = s.trim();
  return t ? t.split(/\s+/).length : 0;
}

const styles: Record<string, React.CSSProperties> = {
  picked: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 8,
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    background: "var(--surface)",
  },
  pickedThumb: {
    width: 44,
    height: 44,
    borderRadius: "var(--radius-sm)",
    objectFit: "cover",
    flexShrink: 0,
  },
  pickedThumbEmpty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    color: "var(--text-muted)",
    background: "var(--surface-hover)",
    textAlign: "center",
  },
  hits: {
    display: "flex",
    flexDirection: "column",
    marginTop: 4,
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    overflow: "hidden",
  },
  hit: {
    textAlign: "left",
    padding: "7px 10px",
    background: "var(--surface)",
    border: "none",
    borderBottom: "1px solid var(--border)",
    color: "var(--text-primary)",
    font: "inherit",
    fontSize: 13,
    cursor: "pointer",
  },
  linkBtn: {
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    fontSize: 12,
    color: "var(--accent)",
    cursor: "pointer",
    textDecoration: "underline",
  },
  preview: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 8,
  },
  previewImg: {
    width: 92,
    height: 92,
    objectFit: "cover",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--border)",
  },
  crumb: { marginBottom: 12 },
  crumbLink: {
    color: "var(--text-muted)",
    textDecoration: "none",
    fontSize: 13,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
  },
  h1: { fontSize: 22, fontWeight: 700, margin: 0 },
  cols: { display: "flex", gap: 16, alignItems: "flex-start" },
  side: { width: 320, flexShrink: 0 },
};
